import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Lock, ArrowRight, Plus, X, Save, Send, StickyNote, Trash2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ["#fde68a","#bfdbfe","#bbf7d0","#fbcfe8","#fed7aa","#ddd6fe"];

function Starfield() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d"); let raf;
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize(); window.addEventListener("resize", resize);
    const stars = Array.from({length: 220}, () => ({x:Math.random()*c.width,y:Math.random()*c.height,r:Math.random()*1.4+0.2,a:Math.random()*0.7+0.3,sp:Math.random()*0.2+0.05}));
    const draw = () => {
      ctx.clearRect(0,0,c.width,c.height);
      const g = ctx.createRadialGradient(c.width*.5,c.height*.35,0,c.width*.5,c.height*.35,c.width*.9);
      g.addColorStop(0,"rgba(11,59,34,1)"); g.addColorStop(.6,"rgba(4,20,10,1)"); g.addColorStop(1,"rgba(2,8,4,1)");
      ctx.fillStyle=g; ctx.fillRect(0,0,c.width,c.height);
      stars.forEach(s=>{ s.y-=s.sp*0.08; if(s.y<0){s.y=c.height;s.x=Math.random()*c.width;}
        ctx.save(); ctx.globalAlpha=s.a; ctx.fillStyle=`rgba(220,240,200,${s.a})`;
        ctx.beginPath(); ctx.arc(s.x,s.y,s.r,0,Math.PI*2); ctx.fill(); ctx.restore();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="fixed inset-0 z-0 pointer-events-none"/>;
}

export default function NovaPublicPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [session, setSession] = useState(localStorage.getItem(`nova_session_${token}`));
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/nova/public/${token}/info`)
      .then((r) => r.ok ? r.json() : Promise.reject("Campaña no disponible"))
      .then(setInfo)
      .catch((e) => setError(String(e)));
  }, [token]);

  return (
    <div className="min-h-screen relative overflow-x-hidden text-white" style={{fontFamily:"-apple-system,BlinkMacSystemFont,Inter,sans-serif"}}>
      <Starfield/>
      <div className="relative z-10 px-4 py-10 md:px-10 max-w-7xl mx-auto">
        <Header info={info}/>
        {error && <div className="mt-12 rounded-2xl p-6 bg-red-500/10 border border-red-500/30 text-red-200 max-w-xl mx-auto">{error}</div>}
        {!error && info && !session && <GateForm token={token} onAuth={(s) => { setSession(s); localStorage.setItem(`nova_session_${token}`, s); }}/>}
        {!error && info && session && <Workspace publicToken={token} session={session} info={info} onLogout={() => { localStorage.removeItem(`nova_session_${token}`); setSession(null); }}/>}
      </div>
    </div>
  );
}

function Header({ info }) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}
        className="inline-flex items-center gap-2 text-emerald-300 text-[11px] uppercase tracking-[0.35em] font-semibold mb-3">
        <Sparkles className="w-4 h-4"/> NOVA · Innovation OS · Valoriza
      </motion.div>
      <motion.h1 initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.1}}
        className="text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
        {info?.name || "Identificación de Retos"}
      </motion.h1>
      {info && <motion.p initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.25}} className="text-emerald-100/80 mt-4 text-base md:text-lg max-w-2xl mx-auto">
        {info.description}
      </motion.p>}
    </div>
  );
}

function GateForm({ token, onAuth }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/public/${token}/auth`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ password: pwd })
      });
      if (!r.ok) throw new Error("Contraseña incorrecta");
      const d = await r.json();
      onAuth(d.session);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };
  return (
    <motion.form onSubmit={submit} initial={{opacity:0,y:14}} animate={{opacity:1,y:0}} transition={{delay:0.3}}
      className="mt-14 max-w-md mx-auto rounded-3xl p-7 backdrop-blur-xl"
      style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",boxShadow:"0 30px 60px rgba(0,0,0,0.4)"}}>
      <div className="flex items-center gap-2 text-emerald-200 text-xs uppercase tracking-widest mb-2 font-semibold">
        <Lock className="w-3.5 h-3.5"/> Acceso a la sesión
      </div>
      <label className="text-white/65 text-xs">Contraseña</label>
      <input
        type="password" autoFocus value={pwd} onChange={(e)=>setPwd(e.target.value)}
        data-testid="public-password"
        className="w-full mt-1 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-emerald-400/40 focus:ring-1 focus:ring-emerald-400/40 outline-none"
        placeholder="••••••••"/>
      {err && <div className="text-red-300 text-xs mt-2">{err}</div>}
      <button type="submit" disabled={loading} data-testid="public-submit"
        className="mt-5 w-full py-3 rounded-2xl text-white font-semibold flex items-center justify-center gap-2"
        style={{background:"linear-gradient(135deg,#00873a,#3BAD2D)",boxShadow:"0 15px 30px rgba(0,135,58,0.35)"}}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Entrar al espacio <ArrowRight className="w-4 h-4"/></>}
      </button>
      <p className="text-center text-white/35 text-xs mt-4">Recibiste la contraseña por email del equipo de Innovación.</p>
    </motion.form>
  );
}

function Workspace({ publicToken, session, info, onLogout }) {
  const [view, setView] = useState("form"); // form | board | thanks
  return (
    <div className="mt-10">
      <div className="flex items-center justify-center gap-1 bg-white/5 backdrop-blur rounded-full p-1 w-fit mx-auto border border-white/10">
        <ViewBtn active={view==="form"} onClick={()=>setView("form")} data-testid="view-form">Formulario WOW</ViewBtn>
        <ViewBtn active={view==="board"} onClick={()=>setView("board")} data-testid="view-board">Mural visual</ViewBtn>
      </div>
      <div className="mt-8">
        {view==="form" && <FormView publicToken={publicToken} session={session} info={info} onSent={()=>setView("thanks")}/>}
        {view==="board" && <BoardView publicToken={publicToken} session={session} info={info}/>}
        {view==="thanks" && <Thanks/>}
      </div>
      <div className="text-center mt-10">
        <button onClick={onLogout} className="text-white/40 text-xs hover:text-white/70">Salir de la sesión</button>
      </div>
    </div>
  );
}

function ViewBtn({active, children, ...rest}) {
  return <button {...rest} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${active?"bg-emerald-500 text-white":"text-white/70 hover:text-white"}`}>{children}</button>;
}

function FormView({ publicToken, session, info, onSent }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [challenges, setChallenges] = useState([{title:""}]);
  const [ideas, setIdeas] = useState([{title:""}]);
  const [interestsText, setInterestsText] = useState("");
  const [sending, setSending] = useState(false);

  const addRow = (set, list) => set([...list, {title:""}]);
  const updateRow = (set, list, i, val) => { const cp = [...list]; cp[i] = val; set(cp); };
  const rmRow = (set, list, i) => set(list.filter((_,j)=>j!==i));

  const submit = async () => {
    setSending(true);
    try {
      const payload = {
        full_name: fullName || null, email: email || null,
        challenges: challenges.filter(c=>c.title?.trim()),
        ideas: ideas.filter(c=>c.title?.trim()),
        interests: interestsText.split(",").map(s=>s.trim()).filter(Boolean),
      };
      const r = await fetch(`${BACKEND_URL}/api/nova/public/${publicToken}/submit?session=${session}`, {
        method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(payload)
      });
      if (r.ok) onSent();
    } finally { setSending(false); }
  };

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} className="max-w-3xl mx-auto space-y-6">
      {/* Brief */}
      <div className="rounded-3xl p-6 backdrop-blur-xl" style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)"}}>
        <div className="text-emerald-300 text-xs uppercase tracking-widest font-semibold mb-2">Brief</div>
        <div className="text-white/85 text-sm leading-relaxed prose prose-invert" dangerouslySetInnerHTML={{__html: info?.brief_html || ""}}/>
      </div>

      <div className="rounded-3xl p-6 backdrop-blur-xl space-y-5" style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)"}}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Tu nombre (opcional)" value={fullName} onChange={setFullName} testid="full-name"/>
          <Input label="Email (opcional)" value={email} onChange={setEmail} testid="email"/>
        </div>

        <Section title="Retos que detectas" hint="Plantea preguntas «¿Cómo podríamos…?» o problemas concretos del día a día.">
          {challenges.map((c,i)=>(
            <div key={i} className="flex gap-2">
              <input className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm" value={c.title} onChange={(e)=>updateRow(setChallenges,challenges,i,{...c,title:e.target.value})} placeholder={i===0?"Ej: ¿Cómo podríamos automatizar la detección de impropios?":"Otro reto"} data-testid={`reto-${i}`}/>
              {challenges.length>1 && <button onClick={()=>rmRow(setChallenges,challenges,i)} className="text-white/40 hover:text-red-400"><X className="w-4 h-4"/></button>}
            </div>
          ))}
          <button onClick={()=>addRow(setChallenges,challenges)} className="text-emerald-300 text-xs flex items-center gap-1 hover:text-emerald-200"><Plus className="w-3 h-3"/> Añadir otro reto</button>
        </Section>

        <Section title="Ideas o propuestas" hint="¿Tienes alguna solución en mente?">
          {ideas.map((c,i)=>(
            <div key={i} className="flex gap-2">
              <input className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm" value={c.title} onChange={(e)=>updateRow(setIdeas,ideas,i,{...c,title:e.target.value})} placeholder="Ej: Sensorización común de flota" data-testid={`idea-${i}`}/>
              {ideas.length>1 && <button onClick={()=>rmRow(setIdeas,ideas,i)} className="text-white/40 hover:text-red-400"><X className="w-4 h-4"/></button>}
            </div>
          ))}
          <button onClick={()=>addRow(setIdeas,ideas)} className="text-emerald-300 text-xs flex items-center gap-1 hover:text-emerald-200"><Plus className="w-3 h-3"/> Añadir otra idea</button>
        </Section>

        <Section title="Tus intereses (separados por coma)" hint="Temas que te apasionan para futuras campañas.">
          <input className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm" value={interestsText} onChange={(e)=>setInterestsText(e.target.value)} placeholder="IA, IoT, automatización, ESG…" data-testid="interests"/>
        </Section>

        <button onClick={submit} disabled={sending} data-testid="public-form-send"
          className="w-full py-3 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2"
          style={{background:"linear-gradient(135deg,#00873a,#3BAD2D)",boxShadow:"0 15px 30px rgba(0,135,58,0.35)"}}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Send className="w-4 h-4"/> Enviar mi aportación</>}
        </button>
      </div>
    </motion.div>
  );
}

function Section({title,hint,children}) {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-semibold">{title}</div>
        {hint && <div className="text-xs text-white/45">{hint}</div>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Input({label, value, onChange, testid}) {
  return (
    <label className="block">
      <span className="text-xs text-white/60">{label}</span>
      <input className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm" value={value} onChange={(e)=>onChange(e.target.value)} data-testid={testid}/>
    </label>
  );
}

function BoardView({ publicToken, session, info }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dragRef = useRef({});
  const boardRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/nova/public/${publicToken}/board?session=${session}`)
      .then(r=>r.json()).then(d => { setNotes(d.notes||[]); setLoading(false); });
  }, [publicToken, session]);

  const save = async (n = notes) => {
    setSaving(true);
    await fetch(`${BACKEND_URL}/api/nova/public/${publicToken}/board?session=${session}`, {
      method:"PUT", headers:{"Content-Type":"application/json"}, body: JSON.stringify({notes:n})
    });
    setSaving(false);
  };

  const addNote = () => {
    const color = COLORS[Math.floor(Math.random()*COLORS.length)];
    const n = { id: Math.random().toString(36).slice(2), x: 60 + Math.random()*200, y: 60 + Math.random()*200, color, text:"Nuevo post-it", area: info?.area };
    const next = [...notes, n]; setNotes(next); save(next);
  };
  const updateText = (id, text) => setNotes(notes.map(n => n.id===id ? {...n, text} : n));
  const removeNote = (id) => { const next = notes.filter(n=>n.id!==id); setNotes(next); save(next); };

  const onDrag = (id, e) => {
    const r = boardRef.current.getBoundingClientRect();
    const start = dragRef.current[id];
    const x = e.clientX - r.left - start.dx;
    const y = e.clientY - r.top - start.dy;
    setNotes(notes => notes.map(n => n.id===id ? {...n, x: Math.max(0,Math.min(r.width-180,x)), y: Math.max(0,Math.min(r.height-140,y))} : n));
  };
  const onDragStart = (id, e) => {
    const r = boardRef.current.getBoundingClientRect();
    const note = notes.find(n=>n.id===id);
    dragRef.current[id] = { dx: e.clientX - r.left - note.x, dy: e.clientY - r.top - note.y };
  };
  const onDragEnd = () => save();

  if (loading) return <div className="text-center text-white/60">Cargando mural…</div>;

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-emerald-200 text-xs uppercase tracking-widest font-semibold">
          <StickyNote className="w-3.5 h-3.5"/> Mural colaborativo · {notes.length} post-its
        </div>
        <div className="flex items-center gap-2">
          {saving && <span className="text-emerald-300 text-xs flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin"/> guardando</span>}
          <button onClick={addNote} data-testid="add-postit" className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600">
            <Plus className="w-3.5 h-3.5"/> Añadir post-it
          </button>
        </div>
      </div>
      <div ref={boardRef} className="relative rounded-3xl overflow-hidden" style={{minHeight:"560px",background:"radial-gradient(ellipse at top, rgba(59,173,45,0.08), transparent 60%), repeating-linear-gradient(0deg, rgba(255,255,255,.03) 0 1px, transparent 1px 40px), repeating-linear-gradient(90deg, rgba(255,255,255,.03) 0 1px, transparent 1px 40px), #0a1a10",border:"1px solid rgba(255,255,255,0.08)"}}>
        {notes.map(n => (
          <motion.div
            key={n.id}
            drag
            dragMomentum={false}
            onDragStart={(e)=>onDragStart(n.id,e)}
            onDrag={(e)=>onDrag(n.id,e)}
            onDragEnd={onDragEnd}
            initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}}
            className="absolute group cursor-grab active:cursor-grabbing"
            style={{left:n.x, top:n.y, width:180}}
            data-testid={`postit-${n.id}`}
          >
            <div className="rounded-lg shadow-2xl p-3 text-slate-900" style={{background:n.color, transform:"rotate(-1deg)"}}>
              <textarea
                value={n.text}
                onChange={(e)=>updateText(n.id, e.target.value)}
                onBlur={()=>save()}
                className="w-full bg-transparent text-sm font-medium resize-none outline-none"
                rows={4}
              />
              <button onClick={()=>removeNote(n.id)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-800 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Trash2 className="w-3 h-3"/>
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="text-center text-white/40 text-xs mt-3">Arrastra los post-its donde quieras · doble-click para editar · todo se guarda automáticamente</div>
    </motion.div>
  );
}

function Thanks() {
  return (
    <motion.div initial={{opacity:0,scale:0.9}} animate={{opacity:1,scale:1}} className="max-w-xl mx-auto text-center mt-10 rounded-3xl p-10 backdrop-blur-xl"
      style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)"}}>
      <div className="text-6xl mb-3">🙂</div>
      <h2 className="text-3xl font-black">Millones de gracias</h2>
      <p className="text-emerald-100/80 mt-3">Tu aportación ha llegado al equipo de Innovación de Valoriza. Te avisaremos cuando esté procesada y aparezca matcheada con el portfolio.</p>
      <p className="text-white/40 text-xs mt-6">Puedes cerrar esta pestaña.</p>
    </motion.div>
  );
}
